import React, {useEffect, useMemo, useState} from 'react';
import * as Yup from 'yup';
import {GridPaginationModel} from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Tooltip,
  Stack,
  IconButton,
  Chip,
} from '@mui/material';
import {CheckCircle, Delete, Error} from '@mui/icons-material';
import {useMutation} from '@tanstack/react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {
  IStudentMusicSelectQueryParms,
  useRqListMusicsInGenresByStudentToken,
} from '../../../react_query/music_selections/studentSelections/ListMusicsInGenresByToken';
import Toast from '../../../package/Toaster';
import {
  IStoreStudentSelectedSongs,
  StoreStudentSelectedSongs,
} from '../../../react_query/music_selections/studentSelections/StoreStudentSelectedSongs';
import {IMediaTrack} from '../../../dto/MediaTrack.dto';
import {VerifyStudentToken} from '../../../react_query/music_selections/studentSelections/VerifyStudentToken';
import {useRqUnauthStudentFormDetailsByStudentToken} from '../../../react_query/music_selections/studentSelections/UnauthStudentFormdetails';
import InputSearch from '../../../components/styles/InputSearch';
import {SongSelectionRemoveButton} from '../../../components/Button/SongSelectionRemoveButton';
import {SongSelectionAddButton} from '../../../components/Button/SongSelectionAddButton';
import MusicSelectionDataGrid, {
  MusicSelectionTiles,
} from './MusicSelectionDataGrid';
import StudentMusicSelectionDialog from './StudentMusicSelectionDialog';

const StudentMusicSelection = () => {
  const {token} = useParams();
  const navigate = useNavigate();
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [songLimit, setSongLimit] = useState(0);
  const [search, setSearch] = React.useState('');
  const [studentName, setStudentName] = React.useState('');
  const [selectedSongs, setSelectedSongs] = React.useState<IMediaTrack[]>([]);
  const [selectedSongIds, setSelectedSongIds] = React.useState<number[]>([]);
  const [pagination, setPagination] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [query, setQuery] = React.useState<IStudentMusicSelectQueryParms>({
    page: 1,
    per_page: 10,
    search: '',
  });

  const {data, isFetching} = useRqListMusicsInGenresByStudentToken(
    token ?? '',
    query,
  );

  const {
    data: formData,
    isFetching: formIsFetching,
    isError: formIsError,
  } = useRqUnauthStudentFormDetailsByStudentToken(token ?? '');

  const store = useMutation({
    mutationFn: (payload: IStoreStudentSelectedSongs) => {
      return StoreStudentSelectedSongs(token ?? '', payload);
    },
    onSuccess: response => {
      Toast(response?.message, 'success');
      navigate(`/student-selection/${token}/success-msg`);
    },
    onSettled() {
      setLoading(false);
    },
  });

  const handleSubmit = React.useCallback(() => {
    setLoading(true);
    const selected_songs = selectedSongs.map(song => {
      return {
        media_track_id: song.id,
        track_artist: song.track_artist,
        track_name: song.track_name,
      };
    });
    store.mutate({
      name: studentName,
      selected_songs,
    });
  }, [selectedSongs, store, studentName]);

  useEffect(() => {
    if (!formIsFetching && !formIsError && formData) {
      setSongLimit(formData.song_count);
    }
  }, [formData, formIsError, formIsFetching]);

  React.useEffect(() => {
    setQuery(prevVal => ({
      ...prevVal,
      page: pagination.page + 1,
      per_page: pagination.pageSize,
      search: search,
    }));
  }, [pagination.page, pagination.pageSize, search]);

  React.useEffect(() => {
    const fetchData = async () => {
      const verifyStatus = await VerifyStudentToken(token ?? '');
      if (verifyStatus !== 200)
        navigate(`/student-selection/${token}/error-msg`);
    };
    fetchData();
  }, [navigate, token]);

  const invalidSongsCount = useMemo(
    () => selectedSongIds.length === 0 || selectedSongIds.length > songLimit,
    [selectedSongIds.length, songLimit],
  );

  return (
    <Box className='h-full' sx={{py: {xs: 2, sm: 4}}}>
      <Container
        sx={{
          background: 'var(--blue-variant6)',
          paddingX: {xs: 1, sm: 4},
          py: {xs: 1, sm: 4},
        }}
        maxWidth='xl'
        className='radius-10 text-center h-full'>
        <Header />
        <StudentNameCmpt
          studentName={studentName}
          setStudentName={setStudentName}
        />
        <Box
          sx={{background: 'var(--blue_variant29)'}}
          className='radius-10 pt-4 mt-4 calcheight-168'>
          {/* Header - 40px, StudentName Input - 40px, Self-margin-top - 16px, bottom-buttons - 72px => Total - 168px */}
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={1}
            className='h-50'
            px={1}>
            <SearchBoxCmpt search={search} setSearch={setSearch} />
            <Chip
              label={`${selectedSongIds.length}/${songLimit}`}
              icon={
                invalidSongsCount ? (
                  <Error color='warning' />
                ) : (
                  <CheckCircle color='success' />
                )
              }
            />
          </Stack>
          <Box sx={{display: {xs: 'none', sm: 'block'}}}>
            <MusicSelectionDataGrid
              data={data?.data ?? []}
              isFetching={isFetching}
              pagination={pagination}
              selectedSongIds={selectedSongIds}
              setPagination={setPagination}
              setSelectedSongIds={setSelectedSongIds}
              setSelectedSongs={setSelectedSongs}
              total={data?.meta.total || 0}
            />
          </Box>
          <Box
            sx={{display: {xs: 'block', sm: 'none'}}}
            className='calcheight-50'>
            <MusicSelectionTiles
              data={data?.data ?? []}
              isFetching={isFetching}
              pagination={pagination}
              selectedSongIds={selectedSongIds}
              setPagination={setPagination}
              setSelectedSongIds={setSelectedSongIds}
              setSelectedSongs={setSelectedSongs}
              total={data?.meta.total || 0}
            />
          </Box>
        </Box>
        {isPreview && (
          <StudentMusicSelectionDialog
            onClose={() => setIsPreview(false)}
            selectedSongIds={selectedSongIds}
            selectedSongs={selectedSongs}
            setSelectedSongIds={setSelectedSongIds}
            setSelectedSongs={setSelectedSongs}
          />
        )}
        <Stack
          direction='row'
          className='h-72'
          spacing={2}
          py={2}
          justifyContent='center'>
          <Button
            variant='contained'
            disabled={!selectedSongIds.length}
            onClick={() => setIsPreview(true)}>
            Preview
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={invalidSongsCount || studentName === '' || loading}>
            Submit
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default StudentMusicSelection;

export const MediaTrackSelection = ({
  track,
  selectedSongIds,
  setSelectedSongIds,
  setSelectedSongs,
  isDeleteIcon = false,
}: {
  track: IMediaTrack;
  selectedSongIds: number[];
  setSelectedSongs: React.Dispatch<React.SetStateAction<IMediaTrack[]>>;
  setSelectedSongIds: React.Dispatch<React.SetStateAction<number[]>>;
  isDeleteIcon?: boolean;
}) => {
  const handleSongAdd = React.useCallback(
    (song: IMediaTrack) => {
      setSelectedSongIds(prev => {
        return [...prev, song.id];
      });
      setSelectedSongs(prev => {
        return [...prev, song];
      });
    },
    [setSelectedSongIds, setSelectedSongs],
  );

  const handleSongRemove = React.useCallback(
    (song: IMediaTrack) => {
      setSelectedSongIds(prev => {
        return prev.filter(id => id !== song.id);
      });
      setSelectedSongs(prev => {
        return prev.filter(prevSong => prevSong.id !== song.id);
      });
    },
    [setSelectedSongIds, setSelectedSongs],
  );

  return selectedSongIds?.includes(track.id) ? (
    <>
      {isDeleteIcon ? (
        <IconButton
          aria-label='delete-song'
          onClick={() => handleSongRemove(track)}
          color='error'>
          <Delete />
        </IconButton>
      ) : (
        <SongSelectionRemoveButton onClick={() => handleSongRemove(track)} />
      )}
    </>
  ) : (
    <SongSelectionAddButton onClick={() => handleSongAdd(track)} />
  );
};

function Header() {
  return (
    <Typography
      variant='h4'
      className='text-center font-bold h-40 overflow-hidden'>
      STUDENT SELECTION FORM
    </Typography>
  );
}

const studentSchema = Yup.string()
  .max(250)
  .test('not-only-spaces', 'Value cannot contain only spaces', value => {
    if (value) {
      return /\S/.test(value);
    }
    return true;
  });

function StudentNameCmpt({
  studentName,
  setStudentName,
}: Readonly<{
  studentName: string;
  setStudentName: React.Dispatch<React.SetStateAction<string>>;
}>) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState('');

  const handleOnChange = React.useCallback(() => {
    if (inputRef.current) {
      const studentInput = inputRef.current.value;
      studentSchema
        .validate(studentInput, {abortEarly: false})
        .then(() => {
          setError('');
          setStudentName(studentInput);
        })
        .catch((err: Yup.ValidationError) => {
          setError(err.message);
        });
    }
  }, [setStudentName]);

  return (
    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      className='h-40'
      justifyContent='center'>
      <TextField
        className='max-w-400 w-full'
        type='text'
        variant='outlined'
        size='small'
        id='student-name'
        label='Student Name *'
        value={studentName}
        inputRef={inputRef}
        helperText={error}
        error={Boolean(error)}
        onChange={handleOnChange}
      />
      {!studentName && (
        <Tooltip title='Student name is required'>
          <Error color='warning' />
        </Tooltip>
      )}
    </Stack>
  );
}

function SearchBoxCmpt({
  search,
  setSearch,
}: Readonly<{
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}>) {
  const handleSearch = React.useCallback(
    (val: string) => {
      setSearch(val);
    },
    [setSearch],
  );

  return (
    <InputSearch
      className='max-w-250'
      placeholder='Search Song'
      value={search}
      onChange={handleSearch}
    />
  );
}
